<template>
  <div>
    <PageHeader
      title="Accounts"
      subtitle="Your Salesforce accounts, matched to practices in the graph and briefed."
      :heading-level="1"
    >
      <template #actions>
        <template v-if="status.connected">
          <button type="button" class="btn-secondary-sm" :disabled="syncing" @click="sync">
            {{ syncing ? 'Syncing…' : 'Sync now' }}
          </button>
          <button type="button" class="btn-link text-sm" @click="disconnect">Disconnect</button>
        </template>
      </template>
    </PageHeader>

    <div class="px-6 py-4 space-y-4">
      <LoadingIndicator v-if="loading" message="Loading accounts..." />

      <div v-else-if="!status.connected" class="bg-white rounded-lg border border-gray-200 px-6 py-12 text-center">
        <div class="text-base font-semibold text-gray-900">Connect Salesforce to bring in your accounts</div>
        <p class="text-sm text-gray-600 mt-1 max-w-xl mx-auto">
          Each Account and Lead becomes a practice here, keyed by its Salesforce id, with its tax id, owner and status.
          We match every one to Medicare data and write a brief for each match. Nothing in your Salesforce changes.
        </p>
        <button type="button" class="btn-primary-sm mt-5" @click="connect">Connect Salesforce</button>
        <p v-if="connectError" class="text-sm text-red-dark mt-3">Salesforce returned: {{ connectError }}</p>
      </div>

      <template v-else>
        <div class="grid grid-cols-[1fr_auto] gap-4 items-start">
          <div class="bg-white rounded-lg border border-gray-200">
            <div class="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-gray-100">
              <div class="text-sm">
                <span class="font-semibold text-gray-900">Connected</span>
                <span class="text-gray-600"> to <a :href="status.instance_url" target="_blank" rel="noopener" class="text-neutral-700 underline decoration-dotted">{{ orgHost }}</a> as {{ status.connected_user }}</span>
              </div>
              <div class="text-xs text-gray-500">
                <span v-if="status.last_sync_at">Last sync {{ fmtWhen(status.last_sync_at) }}</span>
                <span v-else>Not synced yet</span>
              </div>
            </div>
            <div class="flex flex-wrap items-start gap-4 px-4 py-3">
              <label class="inline-flex flex-col gap-0.5">
                <span class="text-xs font-semibold">Tax id field</span>
                <select v-model="settings.tax_id_field" class="px-3 py-1.5 bg-white border border-gray-300 rounded-lg text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500" @focus="loadFields">
                  <option value="">Not stored in Salesforce</option>
                  <option v-for="f in fieldChoices" :key="f.name" :value="f.name">{{ f.label }} ({{ f.name }})</option>
                </select>
              </label>
              <label class="inline-flex flex-col gap-0.5">
                <span class="text-xs font-semibold">NPI field</span>
                <select v-model="settings.npi_field" class="px-3 py-1.5 bg-white border border-gray-300 rounded-lg text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500" @focus="loadFields">
                  <option value="">Not stored in Salesforce</option>
                  <option v-for="f in fieldChoices" :key="f.name" :value="f.name">{{ f.label }} ({{ f.name }})</option>
                </select>
              </label>
              <label class="inline-flex flex-col gap-0.5">
                <span class="text-xs font-semibold">Status field</span>
                <select v-model="settings.status_field" class="px-3 py-1.5 bg-white border border-gray-300 rounded-lg text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500" @focus="loadFields">
                  <option value="">None</option>
                  <option v-for="f in fieldChoices" :key="f.name" :value="f.name">{{ f.label }} ({{ f.name }})</option>
                </select>
              </label>
              <label class="inline-flex items-center gap-2 text-sm text-gray-700 mt-5 cursor-pointer select-none">
                <input v-model="settings.include_leads" type="checkbox" class="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500" />
                Include Leads
              </label>
              <label class="inline-flex flex-col gap-0.5 flex-1 min-w-[220px]">
                <span class="text-xs font-semibold">Only accounts where</span>
                <input v-model="settings.account_filter" type="text" placeholder="e.g. Type = 'Prospect'" class="px-3 py-1.5 bg-white border border-gray-300 rounded-lg text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500" />
              </label>
              <button type="button" class="btn-secondary-sm mt-5" :disabled="savingSettings" @click="saveSettings">
                {{ savingSettings ? 'Saving…' : 'Save' }}
              </button>
            </div>
          </div>

          <div class="bg-white rounded-lg border border-gray-200 px-4 py-3 min-w-[260px]">
            <div class="text-xs font-semibold text-gray-500 mb-2">Coverage</div>
            <div class="grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
              <span class="text-gray-600">Accounts</span><span class="text-right tabular-nums font-semibold text-gray-900">{{ coverage.accounts }}</span>
              <span class="text-gray-600">Matched</span><span class="text-right tabular-nums font-semibold text-gray-900">{{ coverage.matched }}</span>
              <span class="text-gray-600">Need a decision</span><span class="text-right tabular-nums" :class="coverage.ambiguous ? 'font-semibold text-gray-900' : 'text-gray-400'">{{ coverage.ambiguous }}</span>
              <span class="text-gray-600">Not in Medicare data</span><span class="text-right tabular-nums" :class="coverage.unmatched ? 'font-semibold text-gray-900' : 'text-gray-400'">{{ coverage.unmatched }}</span>
              <span class="text-gray-600">Briefed</span><span class="text-right tabular-nums font-semibold text-gray-900">{{ coverage.briefed }}</span>
            </div>
          </div>
        </div>

        <div class="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div class="flex flex-wrap items-start gap-4 px-4 py-3 border-b border-gray-100">
            <FilterTabs
              label="Match"
              :model-value="matchFilter"
              :options="matchOptions"
              @update:model-value="matchFilter = $event"
            />
            <label class="inline-flex flex-col gap-0.5 flex-1 min-w-[200px] max-w-sm">
              <span class="text-xs font-semibold">Search</span>
              <input v-model="search" type="text" placeholder="Account or practice name" class="sensitive px-3 py-1.5 bg-white border border-gray-300 rounded-lg text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500" />
            </label>
          </div>
          <div v-if="!rows.length" class="px-4 py-12 text-center text-sm text-gray-600">
            No accounts yet. Sync to pull them from Salesforce.
          </div>
          <div v-else-if="!filteredRows.length" class="px-4 py-12 text-center text-sm text-gray-600">Nothing matches.</div>
          <AgGridVue
            v-else
            class="accounts-grid"
            :theme="gridTheme"
            :column-defs="columnDefs"
            :default-col-def="defaultColDef"
            :row-data="filteredRows"
            :row-height="52"
            :header-height="40"
            dom-layout="autoHeight"
            :suppress-cell-focus="true"
            :get-row-id="(p) => String(p.data.id)"
            @cell-clicked="onCellClicked"
          />
        </div>
      </template>
    </div>

    <div v-if="picking" class="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/40" @click.self="picking = null">
      <div class="bg-white rounded-lg border border-gray-200 shadow-xl w-[560px] max-w-[92vw]">
        <div class="px-5 py-4 border-b border-gray-100">
          <div class="text-sm font-semibold text-gray-900 sensitive">{{ picking.name }}</div>
          <div class="text-xs text-gray-500 mt-0.5">{{ [picking.city, picking.state].filter(Boolean).join(', ') }} · Salesforce</div>
        </div>
        <div class="px-5 py-3">
          <div class="text-xs font-semibold text-gray-500 mb-2">Which practice is this?</div>
          <div class="divide-y divide-gray-100 border-y border-gray-100">
            <button
              v-for="c in picking.candidates"
              :key="c.practice_key"
              type="button"
              class="w-full text-left flex items-center justify-between gap-3 py-2.5 hover:bg-gray-50 px-2 -mx-2 rounded"
              @click="choose(picking, c)"
            >
              <span class="sensitive text-sm text-gray-900"><span class="sensitive" data-spoof-kind="practice">{{ c.name }}</span><span class="text-gray-500"> · <span class="sensitive">{{ c.state }}</span><span v-if="c.primary_tin"> · TIN <span class="sensitive" data-spoof-kind="identifier">{{ c.primary_tin }}</span></span></span></span>
              <span class="text-xs tabular-nums text-gray-500 shrink-0">{{ c.lives ? fmtInt(c.lives) + ' lives' : '' }}</span>
            </button>
          </div>
        </div>
        <div class="px-5 py-3 flex items-center justify-between">
          <button type="button" class="btn-link text-sm" @click="choose(picking, null)">None of these</button>
          <button type="button" class="btn-secondary-sm" @click="picking = null">Cancel</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { AgGridVue } from 'ag-grid-vue3'
import { themeQuartz } from 'ag-grid-community'
import PageHeader from '@/components/widgetcomponents/library/PageHeader.vue'
import FilterTabs from '@/components/widgetcomponents/library/FilterTabs.vue'
import LoadingIndicator from '@/components/widgetcomponents/library/LoadingIndicator.vue'

const MATCH_LABEL = { matched: 'Matched', ambiguous: 'Needs a decision', unmatched: 'Not found' }
const MATCH_DOT = { matched: 'bg-secondary-800', ambiguous: 'bg-yellow-dark', unmatched: 'bg-gray-300' }

function escapeHtml(s) {
  return String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]))
}

export default {
  name: 'AccountsTab',
  components: { AgGridVue, PageHeader, FilterTabs, LoadingIndicator },
  props: {
    connectResult: { type: String, default: null },
  },
  emits: ['open-brief'],
  data() {
    return {
      loading: true,
      syncing: false,
      savingSettings: false,
      status: { connected: false },
      settings: { tax_id_field: '', npi_field: '', status_field: '', account_filter: '', include_leads: true },
      fieldOptions: [],
      rows: [],
      instanceUrl: '',
      matchFilter: 'all',
      matchOptions: [
        { value: 'all', label: 'All' },
        { value: 'matched', label: 'Matched' },
        { value: 'ambiguous', label: 'Needs a decision' },
        { value: 'unmatched', label: 'Not found' },
      ],
      search: '',
      picking: null,
      gridTheme: themeQuartz.withParams({
        browserColorScheme: 'light',
        headerBackgroundColor: '#F7F7F8',
        headerTextColor: '#626264',
        headerFontSize: 12,
        headerFontWeight: 600,
        fontSize: 13,
        rowBorder: true,
        wrapperBorder: false,
        wrapperBorderRadius: 0,
        cellHorizontalPadding: 16,
        rowHoverColor: '#F7F7F8',
      }),
      defaultColDef: {
        sortable: true,
        resizable: false,
        suppressMovable: true,
        suppressHeaderMenuButton: true,
        suppressHeaderFilterButton: true,
        cellClass: 'cell-theme-left-aligned',
      },
      columnDefs: [
        {
          headerName: 'Salesforce record',
          field: 'name',
          flex: 2,
          minWidth: 220,
          context: { demoSensitive: true },
          cellRenderer: (p) => `<div class="leading-tight"><div class="font-semibold text-gray-900"><span class="sensitive">${escapeHtml(p.data.name)}</span>${p.data.in_network ? '<span class="ml-1.5 inline-flex rounded px-1 text-[10px] font-semibold bg-secondary-200 text-secondary-1000 align-text-bottom" title="This practice is already in your network, so it is left out of Targets">In your network</span>' : ''}</div><div class="text-[11px] text-gray-500 sensitive">${escapeHtml([p.data.city, p.data.state].filter(Boolean).join(', '))}${p.data.owner_name ? ' · ' + escapeHtml(p.data.owner_name) : ''}${p.data.tins && p.data.tins.length ? ' · TIN ' + escapeHtml(p.data.tins.join(', ')) : ''}</div></div>`,
        },
        { headerName: 'Kind', field: 'kind', width: 90, valueFormatter: (p) => (p.value === 'lead' ? 'Lead' : 'Account') },
        { headerName: 'Status', field: 'status', width: 150, cellClass: 'cell-theme-left-aligned sensitive' },
        {
          headerName: 'Match',
          field: 'match_status',
          width: 150,
          cellRenderer: (p) => `<span class="inline-flex items-center gap-2 font-medium ${p.value === 'unmatched' ? 'text-gray-500' : 'text-gray-900'}"><span class="inline-block w-2 h-2 rounded-sm ${MATCH_DOT[p.value]}"></span>${MATCH_LABEL[p.value]}${p.value === 'ambiguous' ? ' <span class="text-neutral-700 font-semibold">Choose</span>' : ''}</span>`,
        },
        {
          headerName: 'Practice in graph',
          field: 'practice_name',
          flex: 2,
          minWidth: 220,
          // Only matched rows draw (and tag) the name.
          context: { demoSensitive: true },
          cellRenderer: (p) => {
            if (p.data.match_status === 'matched') return `<span class="sensitive">${escapeHtml(p.data.practice_name || p.data.practice_key)}</span><span class="text-[11px] text-gray-400 ml-2">${p.data.match_basis === 'tax_id' ? 'by tax id' : p.data.match_basis === 'npi' ? 'by NPI' : p.data.match_basis === 'manual' ? 'chosen' : 'by name'}</span>`
            if (p.data.match_status === 'ambiguous') return `<span class="text-gray-500">${p.data.candidates.length} possible practices</span>`
            return '<span class="text-gray-400">Not in Medicare data</span>'
          },
        },
        {
          headerName: 'Brief',
          field: 'brief',
          width: 190,
          cellRenderer: (p) => {
            if (!p.data.practice_key) return ''
            if (!p.value) return '<span class="text-gray-400">Not written yet</span>'
            return `<span class="text-gray-600">Written ${new Date(p.value.written).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</span><span class="text-neutral-700 font-medium ml-3">Read</span>`
          },
        },
        {
          headerName: '',
          field: 'sf_id',
          width: 48,
          sortable: false,
          cellClass: 'cell-theme-center-aligned',
          cellRenderer: () => '<span class="text-gray-400" title="Open in Salesforce" aria-label="Open in Salesforce"><svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"><path d="M5 2H2.5A.5.5 0 0 0 2 2.5v7a.5.5 0 0 0 .5.5h7a.5.5 0 0 0 .5-.5V7M7 2h3v3M10 2 5.5 6.5"/></svg></span>',
        },
      ],
    }
  },
  computed: {
    coverage() {
      return this.status.coverage || { accounts: 0, matched: 0, ambiguous: 0, unmatched: 0, briefed: 0 }
    },
    orgHost() {
      try {
        return new URL(this.status.instance_url).host
      } catch {
        return this.status.instance_url
      }
    },
    connectError() {
      return this.connectResult && this.connectResult !== 'connected' ? this.connectResult : null
    },
    fieldChoices() {
      if (this.fieldOptions.length) return this.fieldOptions
      return [this.settings.tax_id_field, this.settings.npi_field, this.settings.status_field]
        .filter((name, i, all) => name && all.indexOf(name) === i)
        .map((name) => ({ name, label: name }))
    },
    filteredRows() {
      const q = this.search.trim().toLowerCase()
      return this.rows.filter((r) => {
        if (this.matchFilter !== 'all' && r.match_status !== this.matchFilter) return false
        if (q && !(r.name || '').toLowerCase().includes(q) && !(r.practice_name || '').toLowerCase().includes(q)) return false
        return true
      })
    },
  },
  created() {
    this.load()
  },
  methods: {
    async load() {
      this.loading = true
      try {
        const { data } = await this.$axios.get('/api/growth/salesforce/status/')
        this.status = data
        if (data.connected) {
          this.settings = {
            tax_id_field: data.tax_id_field || '',
            npi_field: data.npi_field || '',
            status_field: data.status_field || '',
            account_filter: data.account_filter || '',
            include_leads: data.include_leads !== false,
          }
          const accounts = await this.$axios.get('/api/growth/salesforce/accounts/')
          this.rows = accounts.data.rows
          this.instanceUrl = accounts.data.instance_url
        }
      } finally {
        this.loading = false
      }
    },
    async loadFields() {
      if (this.fieldOptions.length) return
      const { data } = await this.$axios.get('/api/growth/salesforce/fields/')
      this.fieldOptions = data.filter((f) => ['string', 'textarea', 'picklist', 'phone', 'double', 'int'].includes(f.type))
    },
    async connect() {
      const { data } = await this.$axios.get('/api/growth/salesforce/connect/')
      window.location.assign(data.url)
    },
    async disconnect() {
      await this.$axios.delete('/api/growth/salesforce/connect/')
      this.rows = []
      await this.load()
    },
    async saveSettings() {
      this.savingSettings = true
      try {
        await this.$axios.patch('/api/growth/salesforce/status/', this.settings)
      } finally {
        this.savingSettings = false
      }
    },
    async sync() {
      this.syncing = true
      try {
        await this.$axios.patch('/api/growth/salesforce/status/', this.settings)
        await this.$axios.post('/api/growth/salesforce/sync/')
        await this.load()
      } finally {
        this.syncing = false
      }
    },
    onCellClicked(e) {
      const col = e.colDef.field
      if (col === 'sf_id') {
        window.open(e.data.salesforce_url || `${this.instanceUrl}/${e.data.sf_id}`, '_blank', 'noopener')
        return
      }
      if (col === 'match_status' && e.data.match_status === 'ambiguous') {
        this.picking = e.data
        return
      }
      if (col === 'brief' && e.data.brief) {
        this.$emit('open-brief', { practice_key: e.data.practice_key })
        return
      }
      if (col === 'name') {
        this.$router.push({ name: 'Entity', params: { entity_id: e.data.id } })
      }
    },
    async choose(account, candidate) {
      await this.$axios.post(`/api/growth/salesforce/accounts/${account.id}/match/`, {
        practice_key: candidate ? candidate.practice_key : '',
      })
      this.picking = null
      await this.load()
    },
    fmtInt(v) {
      return v == null ? '' : Math.round(v).toLocaleString()
    },
    fmtWhen(iso) {
      const d = new Date(iso)
      return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) + ' ' + d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
    },
  },
}
</script>

<style scoped>
.accounts-grid :deep(.ag-center-cols-viewport) {
  min-height: 0 !important;
}
</style>
